import React, { useState, useEffect } from 'react';
import { fetchReporteCombustibleRendimiento } from '../../services/Combustible/consumoService';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { formatNumber } from "../../utils/numberUtil";
const saveAs = require('file-saver').saveAs;

interface FuelRecord {
    Placa: string;
    NumeroUnidad: string;
    CentroCosto: string;
    CentroCostoDesc: string;
    Chofer: string;
    OdometroActual: number;
    OdometroAnterior: number | null;
    Recorrido: number;
    Horas: number | null;
    PrecioTotal: number;
    Litros: number;
    FechaRegistro: string;
    RendimientoKmPorLitro: number | null;
    CostoPorKm: number | null;
}

const CombustibleRendimiento: React.FC = () => {
    const [data, setData] = useState<FuelRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(format(new Date().setDate(1), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [placa, setPlaca] = useState('');
    const [unidad, setUnidad] = useState('');

    // Altura del navbar superior si existe (ajústala según tu layout)
    const navbarHeight = 60; // Ejemplo: si tienes un header fijo de 60px

    const fetchReport = async () => {
        if (!startDate || !endDate) {
            alert('Seleccione rango de fechas');
            return;
        }
        setLoading(true);
        try {
            const params: any = { startDate, endDate };
            if (placa.trim()) params.placa = placa;
            if (unidad.trim()) params.unidad = unidad;
            const response = await fetchReporteCombustibleRendimiento(params);
            setData(response.data.consumoRendimiento);
        } catch (error) {
            console.error(error);
            alert('Error al cargar el reporte');
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = () => {
        if (data.length === 0) {
            alert('No hay datos para exportar');
            return;
        }
        // 1. Crear array de arrays con datos planos (sin formatos)
        const headers = [
            'Placa', 'Unidad', 'Centro de Costo', 'Desc. CentroCosto', 'Chofer',
            'Odómetro Ant.', 'Odómetro Act.', 'Recorrido (km)', 
            'Precio Total', 'Litros', 'Rendimiento (km/L)', 'Costo por km', 'Fecha'
        ];

        // Mapear datos: convertir números a número real (sin formatear)
        const rows = data.map(record => [
            record.Placa,
            record.NumeroUnidad,
            record.CentroCosto,
            record.CentroCostoDesc,
            record.Chofer,
            record.OdometroAnterior ?? 0,
            record.OdometroActual,
            record.Recorrido,
            // record.Horas ?? '',
            record.PrecioTotal,
            record.Litros,
            record.RendimientoKmPorLitro ?? '',
            record.CostoPorKm ?? '',
            format(new Date(record.FechaRegistro), 'dd/MM/yyyy')
        ]);


        // 2. Crear hoja a partir del array
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

        // 3. Definir formato para columnas numéricas (índices desde 0)
       
        const numericCols = [5, 6, 7, 8, 9, 10, 11];
        const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:N1');
        for (let row = range.s.r + 1; row <= range.e.r; row++) { // desde segunda fila (datos) hasta última
            for (let col of numericCols) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                const cell = ws[cellAddress];
                if (cell && typeof cell.v === 'number') {
                    cell.t = 'n';                        // tipo número
                    cell.z = '#,##0.00';                 // formato: miles y 2 decimales
                    // Para números enteros (como odómetros) podrías usar '#,##0'
                    if (col === 5 || col === 6 || col === 7) cell.z = '#,##0';
                    if (col === 8 || col === 9 || col === 10 || col === 11) cell.z = '#,##0.00'; // rendimiento y costo también 2 decimales
                }
            }
        }


        // 4. Opcional: ajustar ancho de columnas
        ws['!cols'] = [
            { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 60 }, { wch: 30 },
            { wch: 15 }, { wch: 15 }, { wch: 15 }, /* { wch: 10 }, */ { wch: 15 },
            { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 12 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'ReporteCombustible');
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, `reporte_combustible_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
    };

    useEffect(() => {
        fetchReport();
    }, []);

    return (
        <div className="d-flex flex-column " style={{ height: `calc(100vh - ${navbarHeight}px)` }}>
            {/* Filtros fijos (sticky) */}
            <div className='bg-white pt-2 pb-0 border-bottom shadow-sm flex-shrink-0 z-0'>
                <h2 className='mb-2'>
                    <i className="fas fa-gas-pump me-2"></i>
                    Reporte de Consumo de Combustible
                </h2>
                <div className="row g-3 mb-3 align-items-end">
                    <div className='col-md-2'>
                        <label className="form-label">Fecha desde</label>
                        <input
                            type="date"
                            className='form-control'
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className='col-md-2'>
                        <label className="form-label">Fecha hasta</label>
                        <input
                            className='form-control'
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    <div className='col-md-2'>
                        <label className='form-label'>Placa</label>
                        <input
                            className='form-control'
                            type="text"
                            placeholder="Ej: ABC-123"
                            value={placa}
                            onChange={(e) => setPlaca(e.target.value)}
                        />
                    </div>
                    <div className='col-md-2'>
                        <label className='form-label'>Número de Unidad</label>
                        <input
                            className='form-control'
                            type="text"
                            placeholder="Ej: 001"
                            value={unidad}
                            onChange={(e) => setUnidad(e.target.value)}
                        />
                    </div>
                    <div className="col-md-2">
                        <span className='badge bg-primary text-white'>Registros obtenidos: {data.length}</span>
                    </div>
                    <div className='col-md-2'>
                        <button
                            className='btn btn-primary w-100 mb-2'
                            onClick={fetchReport}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className='spinner-border spinner-border-sm me-2' role="status" aria-hidden="true"></span>
                                    Cargando...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-search me-2"></i> Buscar
                                </>
                            )}
                        </button>
                        <button
                            className="btn btn-success w-100"
                            onClick={exportToExcel}
                            disabled={data.length === 0}
                        >
                            <i className="fas fa-file-excel me-2"></i> Exportar a Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Contenedor de tabla con scroll */}
            <div
                className="flex-grow-1 overflow-auto border-top border-bottom shadow-sm"
            >
                <div className="table-responsive h-100">
                    <table className='table table-striped table-bordered table-hover'>
                        {/* Cabecera sticky con top: 0 relativo al contenedor */}
                        <thead
                            className="table-dark position-sticky z-0"
                            style={{
                                top: 0,
                                zIndex: 1030,
                                backgroundColor: '#212529'
                            }}
                        >
                            <tr>
                                {/* Aplica fondo también a cada th para opacidad total */}
                                {['Placa', 'Unidad', 'Centro Costo', 'Desc. CentroCosto', 'Chofer', 'Odómetro Ant.', 'Odómetro Act.', 'Recorrido (km)',  'Precio Total', 'Litros', 'Rend. (km/L)', 'Costo/km', 'Fecha'].map((col, i) => (
                                    <th key={i} style={{ backgroundColor: '#212529', position: 'sticky', top: 0 }}>{col}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((record, idx) => (
                                <tr key={idx}>
                                    <td>{record.Placa}</td>
                                    <td>{record.NumeroUnidad}</td>
                                    <td>{record.CentroCosto}</td>
                                    <td>{record.CentroCostoDesc}</td>
                                    <td>{record.Chofer}</td>
                                    {/* <td className="text-end">{record.OdometroAnterior ?? '-'}</td> */}
                                    <td className='text-end'>{new Intl.NumberFormat('en-US').format(record.OdometroAnterior ?? 0)}</td>
                                    <td className="text-end">{new Intl.NumberFormat('en-US').format(record.OdometroActual ?? 0)}</td>
                                    <td className="text-end">{new Intl.NumberFormat('en-US').format(record.Recorrido ?? 0)}</td>
                                    {/* <td className="text-end" hidden>{record.Horas ?? '-'}</td> */}
                                    <td className="text-end">{formatNumber(record.PrecioTotal, 2)}</td>
                                    <td className="text-end">{record.Litros.toFixed(2)}</td>
                                    <td className="text-end">{record.RendimientoKmPorLitro?.toFixed(2) ?? '-'}</td>
                                    <td className="text-end">{formatNumber(record.CostoPorKm ?? 0, 2) ?? '-'}</td>
                                    <td>{format(new Date(record.FechaRegistro), 'dd/MM/yyyy')}</td>
                                </tr>
                            ))}
                            {data.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={14} className="text-center">No hay registros</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CombustibleRendimiento;